/* -----------------Globals--------------- */
import { useCallback } from "react";

/* -----------------Helpers & Hooks--------------- */
import useSessionStore from "./useSessionStore";

import { events } from "@constants/tracking";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { useToast } from "@memorang/ui/components/Toast";
import { useMutation } from "@tanstack/react-query";
import { trackCustomEvent } from "analytics";
/* -----------------Types--------------- */
import { useRouter } from "expo-router";
import { fetchSessionItems } from "../api";
import type { ResumeSessionRequest } from "../api/types";
import { findLastUpdatedItemIndex } from "../helpers/session";
import { flattenSessionItems } from "../helpers/session";

export const useResumeSession = () => {
	const router = useRouter();
	const { showToast } = useToast();

	const resumeSessionMutation = useMutation({
		mutationFn: fetchSessionItems,
	});

	const resumeSession = useCallback(
		(request: ResumeSessionRequest) => {
			return resumeSessionMutation.mutateAsync(request);
		},
		[resumeSessionMutation],
	);
	const updateCurrentBlockIndex = useSessionStore(
		(state) => state.updateCurrentBlockIndex,
	);

	const updateProgress = useSessionStore((state) => state.updateProgress);
	const updateProgressForEachBlock = useSessionStore(
		(state) => state.updateProgressForEachBlock,
	);
	const updateSession = useSessionStore((state) => state.updateSession);
	const handleResumeSession = useCallback(
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: need to refactor this function
		async (numericId: number) => {
			try {
				const result = await resumeSession({
					sessionId: numericId,
				});
				const flattenedSessionItems = flattenSessionItems(result.sessionItems);
				if (!flattenedSessionItems.length) {
					const error = new Error(
						"Error resuming session: session list is empty",
					);
					showToast({
						message: "Failed to resume session, try again",
					});
					captureErrorToSentry(error, {
						sessionId: numericId,
						error: serializeErrorForSentry(result),
					});
					router.replace("/home");
					return;
				}

				const { lastUpdatedBlockIndex, lastUpdatedItemIndex } =
					findLastUpdatedItemIndex(flattenedSessionItems);
				const numItems =
					flattenedSessionItems[lastUpdatedBlockIndex]?.length ?? 0;
				if (!numItems) {
					const error = new Error(
						"Error resuming session: flattened session list is empty",
					);
					showToast({
						message: "Failed to resume session, try again",
					});
					captureErrorToSentry(error, {
						sessionId: numericId,
						error: serializeErrorForSentry(result),
					});
					router.replace("/home");
					return;
				}

				const { status } = result;

				const sessionResponse = {
					...result,
					sessionItems: flattenedSessionItems,
				};

				if (status === "COMPLETE") {
					router.replace(`/session/${numericId}/report/`);
				} else {
					trackCustomEvent({
						eventName: events.sessionResumed,
						numericId,
					});
					updateCurrentBlockIndex(lastUpdatedBlockIndex);

					updateSession({
						sessionResponse,
						numItemsRequested: numItems,
						isSummativeTest: result.sessionType !== "FORMATIVE",
						isDiagnostic: false,
					});
					if (lastUpdatedBlockIndex > 0) {
						for (let i = 0; i < lastUpdatedBlockIndex; i++) {
							updateProgressForEachBlock(1, i);
						}
					}

					const progress = (lastUpdatedItemIndex + 1) / numItems;
					updateProgress(progress);

					router.push({
						pathname: "/(protected)/session/[id]",
						params: {
							id: sessionResponse.numericId,
							index: String(lastUpdatedItemIndex) || 1,
						},
					});
				}
			} catch (_error) {
				showToast({
					message: "Failed to resume session, try again",
				});
				const error = new Error("Error resuming session");
				captureErrorToSentry(error, {
					sessionId: numericId,
					error: serializeErrorForSentry(_error),
				});
			}
		},
		[
			router,
			showToast,
			updateCurrentBlockIndex,
			updateProgress,
			updateSession,
			resumeSession,
			updateProgressForEachBlock,
		],
	);

	return {
		handleResumeSession,
	};
};
